import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  AppState,
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '@/store';
import { getMyClasses } from '@/store/slices/myClassesSlice';
import { getStaffClasses } from '@/store/slices/staffClassesSlice';
import { ClassesStackParamList } from '@/types/navigation';
import { MyClass, StaffInstructor } from '@/types/api';
import { useChicagoSeconds } from '@/hooks/useChicagoSeconds';
import { chicagoToday } from '@/utils/scheduleWeek';
import { addDays } from '@/utils/rotations';
import { ClassDay, DayPicker } from '@/components/classes/ClassDay';
import StaffClassesView, { freshStaffFilters } from '@/components/classes/StaffClassesView';
import { colors, font, space, radius } from '@/theme/chalkCourt';

type Nav = StackNavigationProp<ClassesStackParamList, 'ClassesHome'>;

/**
 * "My Classes" tab: on today, the class happening now and the next one with
 * their live rotation (port of the /rotations kiosk rules), then every class
 * the staff member teaches that day. Arrows step through the current
 * Sunday–Saturday week (the active A/B rotation schedule's week).
 */
const MyClassesScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<Nav>();
  const { data, date, isLoading, error } = useSelector((state: RootState) => state.myClasses);
  // Admins (EMPLOYEE.IS_ADMIN) get a Mine | Staff switch; the backend 403s anyone else.
  const isAdmin = useSelector((state: RootState) => !!state.auth?.user?.employee?.admin);
  const staffFilters = useSelector((state: RootState) => state.staffClasses?.filters ?? null);
  const staffList = useSelector((state: RootState) => state.staffClasses?.list ?? null);
  const staffLoading = useSelector((state: RootState) => !!state.staffClasses?.isLoading);
  const [mode, setMode] = useState<'mine' | 'staff'>('mine');
  const staffMode = isAdmin && mode === 'staff';

  // Refetch the shown day on focus (keyed on dispatch only, like CallPerformance);
  // a day held from an earlier week falls back to today.
  const dateRef = useRef(date);
  dateRef.current = date;
  const dataTodayRef = useRef(data?.today);
  dataTodayRef.current = data?.today;
  // Staff refetch only while the Staff view is showing (a non-admin never triggers a 403).
  const staffRef = useRef({ staffMode, staffFilters, staffList });
  staffRef.current = { staffMode, staffFilters, staffList };
  useFocusEffect(
    useCallback(() => {
      const stale = dataTodayRef.current !== undefined && dataTodayRef.current !== chicagoToday();
      dispatch(getMyClasses(stale ? null : dateRef.current));
      const st = staffRef.current;
      if (st.staffMode && st.staffFilters) {
        dispatch(getStaffClasses(freshStaffFilters(st.staffFilters, st.staffList)));
      }
    }, [dispatch])
  );

  // Re-render when the app comes back to the foreground (native resume / PWA tab
  // shown) so a held day from before midnight is noticed even off "today".
  const [, bumpOnResume] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const sub = AppState.addEventListener('change', (s) => s === 'active' && bumpOnResume());
    // react-native-web returns undefined when there's no DOM.
    return () => sub?.remove();
  }, []);

  // The clock keeps ticking while the held day claims to be today, so a
  // midnight rollover re-renders, flags the data stale, and refetches today.
  const heldIsToday = !!data && data.date === data.today;
  const nowSec = useChicagoSeconds(heldIsToday);
  const deviceToday = chicagoToday();
  const stale = !!data && data.today !== deviceToday;
  const isToday = heldIsToday && !stale;
  // At most one refetch per device date: if the server still disagrees (device
  // clock fast or wrong), keep showing its day rather than looping.
  const staleFetchFor = useRef<string | null>(null);
  useEffect(() => {
    if (!stale || isLoading || error || staleFetchFor.current === deviceToday) return;
    staleFetchFor.current = deviceToday;
    dispatch(getMyClasses(null));
  }, [stale, isLoading, error, deviceToday, dispatch]);

  // Step from the requested day (not the shown one) so quick double taps move two days.
  const target = data ? date ?? data.today : null;
  const goDay = (day: string) => {
    if (!data) return;
    dispatch(getMyClasses(day === data.today ? null : day));
  };
  const reload = () => dispatch(getMyClasses(stale ? null : date));
  const openClass = (cls: MyClass) =>
    navigation.navigate('ClassDetail', { classKey: cls.key, title: cls.name });
  const openStaff = (i: StaffInstructor, day: string | null) =>
    navigation.navigate('StaffMember', { employeeId: i.employeeId, name: i.name, date: day });

  const canPrev = !!data && !stale && target! > data.weekStart;
  const canNext = !!data && !stale && target! < data.weekEnd;

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={staffMode ? staffLoading : isLoading && !!data}
          onRefresh={() =>
            staffMode
              ? staffFilters &&
                dispatch(getStaffClasses(freshStaffFilters(staffFilters, staffList)))
              : reload()
          }
          tintColor={colors.accent}
          colors={[colors.accent]}
        />
      }>
      <View style={styles.header}>
        <Text style={styles.kicker}>
          {(staffMode ? staffList?.scheduleName : data?.scheduleName)?.toUpperCase() ?? 'ROTATIONS'}
        </Text>
        <Text style={styles.headline}>{staffMode ? 'STAFF CLASSES' : 'MY CLASSES'}</Text>
        <View style={styles.rule} />
      </View>

      {isAdmin && (
        <View style={styles.modeRow}>
          {(['mine', 'staff'] as const).map((m) => (
            <TouchableOpacity
              key={m}
              onPress={() => setMode(m)}
              style={[styles.modeBtn, mode === m && styles.modeBtnOn]}
              testID={`mode-${m}`}>
              <Text style={[styles.modeText, mode === m && styles.modeTextOn]}>
                {m === 'mine' ? 'MINE' : 'STAFF'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {staffMode ? (
        <StaffClassesView onOpenStaff={openStaff} />
      ) : (
        <>
          <DayPicker
            date={data ? data.date : null}
            isToday={isToday}
            canPrev={canPrev}
            canNext={canNext}
            onPrev={() => target && goDay(addDays(target, -1))}
            onNext={() => target && goDay(addDays(target, 1))}
            onToday={() => data && goDay(data.today)}
          />

          {!data && isLoading && <ActivityIndicator style={styles.loading} color={colors.accent} />}

          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity onPress={reload}>
                <Text style={styles.retry}>Try again</Text>
              </TouchableOpacity>
            </View>
          )}

          {data && (
            <ClassDay
              data={data}
              isToday={isToday}
              nowSec={nowSec}
              onOpen={openClass}
              doneText="You're done teaching for today."
              emptyText="No classes on your schedule for this day."
            />
          )}
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },

  header: { marginBottom: space.md },
  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: -2,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 56,
    lineHeight: 58,
    color: colors.ink,
    letterSpacing: -1,
  },
  rule: {
    height: 3,
    width: 48,
    backgroundColor: colors.ink,
    borderRadius: 2,
    marginTop: space.sm,
  },

  modeRow: { flexDirection: 'row', gap: space.sm, marginBottom: space.md },
  modeBtn: {
    paddingVertical: 6,
    paddingHorizontal: space.lg,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.lineStrong,
  },
  modeBtnOn: { backgroundColor: colors.ink, borderColor: colors.ink },
  modeText: { fontFamily: font.bodyBold, fontSize: 13, letterSpacing: 1.5, color: colors.inkSoft },
  modeTextOn: { color: colors.surface },

  loading: { marginTop: space.xl },
  errorBox: {
    backgroundColor: colors.accentSoft,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
  },
  errorText: { fontFamily: font.body, fontSize: 14, color: colors.accentDark },
  retry: { fontFamily: font.bodyBold, fontSize: 14, color: colors.accentDark, marginTop: space.sm },
});

export default MyClassesScreen;
